import { request, requestJson } from '../services/apiClient';

const SESSION_STORAGE_KEY = 'ta2sheraai.account';
const SESSION_INACTIVITY_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;
const ACCESS_TOKEN_REFRESH_BUFFER_MS = 30 * 1000;
const ACTIVITY_STORAGE_THROTTLE_MS = 15 * 1000;

const accountListeners = new Set();
let activityTrackingInitialized = false;
let refreshPromise = null;
let lastPersistedActivityAt = 0;

function decodeBase64Url(value) {
  const normalizedValue = value.replace(/-/g, '+').replace(/_/g, '/');
  const padding = '='.repeat((4 - (normalizedValue.length % 4)) % 4);

  return atob(`${normalizedValue}${padding}`);
}

function parseJwtPayload(token) {
  if (typeof token !== 'string') {
    return null;
  }

  const [, payload] = token.split('.');

  if (!payload) {
    return null;
  }

  try {
    return JSON.parse(decodeBase64Url(payload));
  } catch {
    return null;
  }
}

export function isValidJwtToken(token) {
  return getJwtExpiryTime(token) > Date.now();
}

function getJwtExpiryTime(token) {
  const payload = parseJwtPayload(token);

  if (!payload) {
    return 0;
  }

  if (payload.exp == null) {
    return Number.POSITIVE_INFINITY;
  }

  if (typeof payload.exp !== 'number') {
    return 0;
  }

  return payload.exp * 1000;
}

function notifyAccountListeners(account) {
  accountListeners.forEach((listener) => listener(account));
}

function normalizeAccount(account) {
  if (!account?.accountId || !account?.token) {
    return null;
  }

  return {
    ...account,
    refreshToken:
      typeof account.refreshToken === 'string' && account.refreshToken.trim()
        ? account.refreshToken.trim()
        : undefined,
    refreshTokenExpiresAtUtc: account.refreshTokenExpiresAtUtc ?? undefined,
    lastActivityAt: Number.isFinite(account.lastActivityAt) ? account.lastActivityAt : Date.now(),
  };
}

function isInactive(account) {
  const lastActivityAt = Number.isFinite(account?.lastActivityAt) ? account.lastActivityAt : 0;
  return !lastActivityAt || Date.now() - lastActivityAt > SESSION_INACTIVITY_WINDOW_MS;
}

function canRestoreAccount(account) {
  if (!account?.accountId || !account?.token) {
    return false;
  }

  if (isValidJwtToken(account.token)) {
    return true;
  }

  return !isInactive(account);
}

export function loadStoredAccount() {
  const serializedAccount = window.localStorage.getItem(SESSION_STORAGE_KEY);

  if (!serializedAccount) {
    return null;
  }

  try {
    const account = normalizeAccount(JSON.parse(serializedAccount));

    if (!account || !canRestoreAccount(account)) {
      window.localStorage.removeItem(SESSION_STORAGE_KEY);
      return null;
    }

    return account;
  } catch {
    window.localStorage.removeItem(SESSION_STORAGE_KEY);
    return null;
  }
}

export function storeAccount(account) {
  const normalizedAccount = normalizeAccount(account);

  if (!normalizedAccount || (!isValidJwtToken(normalizedAccount.token) && isInactive(normalizedAccount))) {
    clearStoredAccount();
    return;
  }

  lastPersistedActivityAt = normalizedAccount.lastActivityAt;
  window.localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(normalizedAccount));
  notifyAccountListeners(normalizedAccount);
}

export function clearStoredAccount() {
  window.localStorage.removeItem(SESSION_STORAGE_KEY);
  refreshPromise = null;
  lastPersistedActivityAt = 0;
  notifyAccountListeners(null);
}

export function subscribeToAccountChanges(listener) {
  accountListeners.add(listener);
  return () => accountListeners.delete(listener);
}

export function markSessionActive(force = false) {
  const account = loadStoredAccount();
  if (!account) {
    return;
  }

  const now = Date.now();
  if (!force && now - lastPersistedActivityAt < ACTIVITY_STORAGE_THROTTLE_MS) {
    return;
  }

  storeAccount({
    ...account,
    lastActivityAt: now,
  });
}

export function initializeAuthSession() {
  if (activityTrackingInitialized || typeof window === 'undefined') {
    return;
  }

  activityTrackingInitialized = true;

  const recordActivity = () => markSessionActive();

  ['pointerdown', 'keydown', 'touchstart', 'focus'].forEach((eventName) => {
    window.addEventListener(eventName, recordActivity, { passive: true });
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      markSessionActive(true);
    }
  });
}

function isAccessTokenFresh(account) {
  return getJwtExpiryTime(account?.token) - Date.now() > ACCESS_TOKEN_REFRESH_BUFFER_MS;
}

function buildRefreshPayload(account) {
  return {
    accountId: account.accountId,
    refreshToken: account.refreshToken,
  };
}

async function refreshStoredAccount() {
  const currentAccount = loadStoredAccount();
  if (!currentAccount || isInactive(currentAccount)) {
    clearStoredAccount();
    throw new Error('Your session expired. Please sign in again.');
  }

  let refreshedAccount;
  try {
    refreshedAccount = await requestJson('/accounts/refresh', {
      method: 'POST',
      body: buildRefreshPayload(currentAccount),
    });
  } catch (error) {
    if (error?.status === 401) {
      clearStoredAccount();
      throw new Error('Your session expired. Please sign in again.');
    }

    throw error;
  }

  const nextAccount = {
    ...currentAccount,
    ...refreshedAccount,
    lastActivityAt: Date.now(),
  };

  storeAccount(nextAccount);
  return nextAccount;
}

export async function ensureAuthenticatedAccount() {
  const account = loadStoredAccount();
  if (!account) {
    throw new Error('Your session expired. Please sign in again.');
  }

  if (!isInactive(account) && isAccessTokenFresh(account)) {
    return account;
  }

  if (!refreshPromise) {
    refreshPromise = refreshStoredAccount().finally(() => {
      refreshPromise = null;
    });
  }

  return refreshPromise;
}

export async function authorizedRequestJson(path, { method = 'GET', body, headers, retryOnUnauthorized = true } = {}) {
  const account = await ensureAuthenticatedAccount();
  const response = await request(path, {
    method,
    headers: {
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      Authorization: `Bearer ${account.token}`,
      ...headers,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (response.status === 401 && retryOnUnauthorized) {
    await refreshStoredAccount();
    return authorizedRequestJson(path, { method, body, headers, retryOnUnauthorized: false });
  }

  if (response.status === 204) {
    return null;
  }

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload.message ?? 'Request failed.');
  }

  return payload;
}

export async function authorizedRequest(path, { method = 'GET', headers, body, retryOnUnauthorized = true } = {}) {
  const account = await ensureAuthenticatedAccount();
  const response = await request(path, {
    method,
    headers: {
      Authorization: `Bearer ${account.token}`,
      ...headers,
    },
    body,
  });

  if (response.status === 401 && retryOnUnauthorized) {
    await refreshStoredAccount();
    return authorizedRequest(path, { method, headers, body, retryOnUnauthorized: false });
  }

  return response;
}

export async function signOutAccount() {
  const account = loadStoredAccount();
  clearStoredAccount();

  if (!account?.accountId) {
    return;
  }

  try {
    await request('/accounts/logout', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(buildRefreshPayload(account)),
    });
  } catch {
    // Ignore logout transport failures because the local session is already cleared.
  }
}
